/**
 * Home page click and view tracking (template `index` only), so GA4 can report
 * views, clicks and click-through rate for every part of the home page, down to
 * the individual tile, card or banner inside each section.
 *
 * homepage_click: one push for every link or button clicked.
 *   click_section  where the click was (see "Section names" below)
 *   click_text     visible text, else image alt / aria-label / title, else
 *                  "(image) /destination-path" (100 chars)
 *   click_url      the link's href ('' for a button)
 *   click_type     'link' or 'button'
 *   section_click  always 1: the count GA4's "Section Clicks" metric sums
 *   Inside a main content section (not header, menu, footer) also:
 *   item_position  the tile/card/banner's position in its section, 1 = first
 *   item_label     its name, e.g. "TOPS", a product name, a banner heading
 *   item_click     always 1: the count GA4's "Item Clicks" metric sums
 *
 * homepage_section_view: one push per section per page load, the first time
 * at least half of it is on screen (or it fills at least half the screen).
 *   click_section, section_view (always 1, "Section Views")
 *
 * homepage_item_view: one push per tile/card/banner per page load, the first
 * time at least half of that item is on screen. Main content sections only.
 * Slides hidden off the side of a carousel only count once scrolled into view.
 *   click_section, item_position, item_label, item_view (always 1, "Item Views")
 * Item CTR in GA4 is Item Clicks / Item Views; Section CTR is Section Clicks /
 * Section Views.
 *
 * An "item" is the largest block around a link whose links all go to the same
 * place: a category tile (image + "SHOP TOPS" text), a product card, a banner.
 * Positions run left to right, top to bottom, in page order.
 *
 * Section names are generic and position-based, so reports read the same week
 * to week whatever the promo is:
 *   Announcement Bar, Header & Menu, Footer, Cart Drawer
 *   Hero Banner      the first content section on the page
 *   Banner 1, 2, ... the image-text banners below it, in order
 *   Hero Slider 1, Image Grid 1, Tile Carousel 1, Product Rail 1, ...
 * Content sections with nothing clickable (spacers) are not counted, so adding
 * a spacer never renumbers anything. The cart drawer is never counted as seen.
 *
 * Goes through dcAnalytics.push() so every push also carries market, currency
 * and customer_id. Loaded deferred after dc-analytics.js by
 * snippets/analytics.liquid. Capture phase and never preventDefault: the
 * click, navigation and any existing tracking (select_promotion, select_item,
 * main_menu_click, sub_menu_click) all proceed. Closing an open <summary> is
 * not counted.
 */
(function () {
  'use strict';
  if (window.__dcHomepageClicks) return;
  window.__dcHomepageClicks = true;

  var CLICKABLE = 'a[href], button, summary, [role="button"]';

  /* Section kind -> generic name. A section's kind is its outer element's tag
     when that is a custom element (<image-text-grid>), else that element's
     first class (<div class="tile-carousel ...">). Anything not listed is
     named from the kind itself, e.g. video-banner -> "Video Banner". */
  var TYPE_NAMES = {
    'image-text-grid': 'Banner',
    itg: 'Banner',
    hero: 'Hero Slider',
    'image-grid': 'Image Grid',
    'image-carousel': 'Image Carousel',
    'tile-carousel': 'Tile Carousel',
    'featured-products': 'Product Rail',
    'product-rail': 'Product Rail',
    'tabbed-content': 'Tabbed Content',
  };

  /* Header, footer and other fixed parts of the layout, by section key. */
  var FIXED_NAMES = {
    'announcement-bar': 'Announcement Bar',
    header: 'Header & Menu',
    footer: 'Footer',
    'cart-drawer': 'Cart Drawer',
  };

  /* Sections never reported as seen: the cart drawer sits off screen until opened. */
  var NO_VIEWS = { 'Cart Drawer': true, Other: true };

  var VIEW_THRESHOLDS = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];

  function isHomePage() {
    var config = window.dcAnalytics && window.dcAnalytics.config;
    return !!(config && config.template === 'index');
  }

  function collapse(text) {
    return String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
  }

  function titleCase(tag) {
    return tag
      .split('-')
      .map(function (w) {
        return w.charAt(0).toUpperCase() + w.slice(1);
      })
      .join(' ');
  }

  function sectionKey(section) {
    var id = section.id.replace(/^shopify-section-/, '');
    var at = id.indexOf('__');
    return at === -1 ? id : id.slice(at + 2);
  }

  function sectionKind(section) {
    var first = section.firstElementChild;
    if (!first) return 'section';
    var tag = first.tagName.toLowerCase();
    if (tag.indexOf('-') !== -1) return tag;
    var cls = String(first.className || '').trim().split(/\s+/)[0];
    return cls || tag;
  }

  function isTemplateSection(section) {
    return /^shopify-section-template--/.test(section.id);
  }

  function isContentSection(section) {
    return isTemplateSection(section) && !!section.querySelector(CLICKABLE);
  }

  /* Hero Banner for the first content section; every later one is named by
     type and numbered within that type, in page order. */
  function contentName(section) {
    var all = document.querySelectorAll('.shopify-section');
    var counts = {};
    var first = true;
    for (var i = 0; i < all.length; i++) {
      var s = all[i];
      if (!isContentSection(s)) continue;
      if (first) {
        first = false;
        if (s === section) return 'Hero Banner';
        continue;
      }
      var kind = sectionKind(s);
      var type = TYPE_NAMES[kind] || titleCase(kind);
      counts[type] = (counts[type] || 0) + 1;
      if (s === section) return type + ' ' + counts[type];
    }
    return 'Other';
  }

  function nameOfSection(section) {
    var key = sectionKey(section);
    if (FIXED_NAMES[key]) return FIXED_NAMES[key];
    if (isTemplateSection(section)) return contentName(section);
    return titleCase(key.replace(/_/g, '-'));
  }

  /* Name of the section an element sits in. */
  function sectionName(el) {
    var section = el.closest('.shopify-section');
    return section ? nameOfSection(section) : 'Other';
  }

  function linkText(el) {
    var text = collapse(el.innerText || el.textContent);
    if (!text) {
      var img = el.querySelector('img[alt]');
      text = collapse(img ? img.getAttribute('alt') : '');
    }
    if (!text) text = collapse(el.getAttribute('aria-label') || el.getAttribute('title'));
    return text;
  }

  function imagePathLabel(href) {
    try {
      return '(image) ' + new URL(href).pathname;
    } catch (e) {
      return '(image)';
    }
  }

  function clickText(el) {
    var text = linkText(el);
    // image-only link with no alt text: name it by where it goes
    if (!text && el.tagName === 'A') text = imagePathLabel(el.href);
    return text.slice(0, 100);
  }

  /* ---------- items (tiles, cards, banners) ---------- */

  /* The largest block around a link, below the section, whose links all go to
     that same place. */
  function itemOf(link, section) {
    var best = link;
    var el = link;
    while (el.parentElement && el.parentElement !== section) {
      var parent = el.parentElement;
      var links = parent.querySelectorAll('a[href]');
      var href = null;
      var mixed = false;
      for (var i = 0; i < links.length; i++) {
        if (href === null) href = links[i].href;
        else if (links[i].href !== href) {
          mixed = true;
          break;
        }
      }
      if (mixed) break;
      best = parent;
      el = parent;
    }
    return boxed(best);
  }

  /* A wrapper with `display: contents` (e.g. a Shopify block div) has no box of
     its own, so it can never be measured as on screen; use the single element
     inside it instead. */
  function boxed(el) {
    while (
      el.childElementCount === 1 &&
      window.getComputedStyle &&
      window.getComputedStyle(el).display === 'contents'
    ) {
      el = el.firstElementChild;
    }
    return el;
  }

  /* Items of a content section in page order, built fresh each time so a
     carousel or rail that re-renders is always read as it is now. */
  function itemsOf(section) {
    var links = section.querySelectorAll('a[href]');
    var items = [];
    for (var i = 0; i < links.length; i++) {
      var item = itemOf(links[i], section);
      if (items.indexOf(item) === -1) items.push(item);
    }
    return items;
  }

  /* Name of an item: the first link text inside it (e.g. "TOPS", a product
     name), else its image description, else where it links. */
  function itemLabel(item) {
    var links = item.matches && item.matches('a[href]') ? [item] : item.querySelectorAll('a[href]');
    for (var i = 0; i < links.length; i++) {
      var text = collapse(links[i].innerText || links[i].textContent);
      if (text) return text.slice(0, 100);
    }
    var img = item.querySelector('img[alt]');
    var alt = collapse(img ? img.getAttribute('alt') : '');
    if (alt) return alt.slice(0, 100);
    for (var j = 0; j < links.length; j++) {
      var aria = collapse(links[j].getAttribute('aria-label') || links[j].getAttribute('title'));
      if (aria) return aria.slice(0, 100);
    }
    return links.length ? imagePathLabel(links[0].href) : '(item)';
  }

  /* Item details for any element inside a main content section, else null. */
  function itemInfo(el) {
    var section = el.closest('.shopify-section');
    if (!section || !isContentSection(section)) return null;
    var items = itemsOf(section);
    for (var i = 0; i < items.length; i++) {
      if (items[i] === el || items[i].contains(el)) {
        return { section: nameOfSection(section), position: i + 1, label: itemLabel(items[i]) };
      }
    }
    return null;
  }

  /* ---------- clicks ---------- */

  function onClick(event) {
    if (!isHomePage()) return;
    var el = event.target && event.target.closest ? event.target.closest(CLICKABLE) : null;
    if (!el) return;
    if (el.tagName === 'SUMMARY' && el.parentElement && el.parentElement.open) return;
    var isLink = el.tagName === 'A';
    var payload = {
      click_section: sectionName(el),
      click_text: clickText(el),
      click_url: isLink ? el.href : '',
      click_type: isLink ? 'link' : 'button',
      section_click: 1,
    };
    var item = itemInfo(el);
    if (item) {
      payload.item_position = item.position;
      payload.item_label = item.label;
      payload.item_click = 1;
    }
    window.dcAnalytics.push('homepage_click', payload);
  }

  document.addEventListener(
    'click',
    function (event) {
      try {
        onClick(event);
      } catch (e) {
        // tracking never breaks the page
      }
    },
    true
  );

  /* ---------- views ---------- */

  function halfSeen(entry) {
    if (!entry.isIntersecting) return false;
    var screen = window.innerHeight || document.documentElement.clientHeight || 0;
    return entry.intersectionRatio >= 0.5 || (screen && entry.intersectionRect.height >= screen * 0.5);
  }

  function startViews() {
    if (!isHomePage()) return;
    if (!('IntersectionObserver' in window) || typeof WeakSet !== 'function') return;

    var seenSections = new WeakSet();
    var sectionObserver = new IntersectionObserver(
      function (entries) {
        try {
          for (var i = 0; i < entries.length; i++) {
            var section = entries[i].target;
            if (seenSections.has(section) || !halfSeen(entries[i])) continue;
            seenSections.add(section);
            sectionObserver.unobserve(section);
            window.dcAnalytics.push('homepage_section_view', {
              click_section: nameOfSection(section),
              section_view: 1,
            });
          }
        } catch (e) {
          // tracking never breaks the page
        }
      },
      { threshold: VIEW_THRESHOLDS }
    );

    var seenItems = new WeakSet();
    var itemObserver = new IntersectionObserver(
      function (entries) {
        try {
          for (var i = 0; i < entries.length; i++) {
            var item = entries[i].target;
            if (seenItems.has(item) || !halfSeen(entries[i])) continue;
            var info = itemInfo(item);
            if (!info) continue;
            seenItems.add(item);
            itemObserver.unobserve(item);
            window.dcAnalytics.push('homepage_item_view', {
              click_section: info.section,
              item_position: info.position,
              item_label: info.label,
              item_view: 1,
            });
          }
        } catch (e) {
          // tracking never breaks the page
        }
      },
      { threshold: VIEW_THRESHOLDS }
    );

    var sections = document.querySelectorAll('.shopify-section');
    for (var i = 0; i < sections.length; i++) {
      var s = sections[i];
      if (!s.querySelector(CLICKABLE)) continue;
      if (NO_VIEWS[nameOfSection(s)]) continue;
      sectionObserver.observe(s);
      if (isContentSection(s)) {
        var items = itemsOf(s);
        for (var j = 0; j < items.length; j++) itemObserver.observe(items[j]);
      }
    }
  }

  try {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startViews, { once: true });
    else startViews();
  } catch (e) {
    // tracking never breaks the page
  }
})();